import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { string } from 'prop-types';
import classNames from 'classnames';
import { Flip, toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

import {
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  IconSocialMediaTiktok,
  IconSocialMediaLinkedin,
  Logo,
  ExternalLink,
  NamedLink,
  Button,
} from '../../components';

import { subscribeToNewsletter } from './Footer.duck';
import { selectIsAuthenticated } from '../../ducks/auth.duck';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { STATUS } from '../../helpers/enums';

import Icons from '../Icons/Icons';

import css from './Footer.module.css';

import footMail from '../../assets/footer/footer-mail.png';
import european from '../../assets/logos/European.jpg';
import eleven from '../../assets/logos/Eleven.jpg';
import frc from '../../assets/logos/FRC-2.png';
import { getDefaultLocale } from '../../util/genericHelpers';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';
import { MIXPANEL_EVENTS } from '../../util/enums';
import { trackAContactUsClicked } from '../../util/mixpanelHelpers';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { selectCurrentUser } from '../../ducks/user.duck';
import { useConsent } from 'react-hook-consent';

const renderSocialMediaLinks = (intl, config) => {
  const { siteFacebookPage, siteInstagramPage, siteLinkedInPage, siteYoutubePage } = config;

  const links = [
    {
      key: 'linkToFacebook',
      href: siteFacebookPage,
      title: intl.formatMessage({ id: 'Footer.goToFacebook' }),
      icon: <IconSocialMediaFacebook />,
      show: true,
    },
    {
      key: 'linkToInstagram',
      href: siteInstagramPage,
      title: intl.formatMessage({ id: 'Footer.goToInstagram' }),
      icon: <IconSocialMediaInstagram className={css.instragram} />,
      show: true,
    },
    {
      key: 'linkToYoutube',
      href: 'https://www.tiktok.com/@getvivianlab',
      title: intl.formatMessage({ id: 'Footer.goToYoutube' }),
      icon: <IconSocialMediaTiktok className={css.tiktok} />,
      show: true,
    },
    {
      key: 'linkToLinkedIn',
      href: siteLinkedInPage,
      title: intl.formatMessage({ id: 'Footer.goToLinkedIn' }),
      icon: <IconSocialMediaLinkedin className={css.linkedin} />,
      show: true,
    },
  ];

  return links
    .filter(({ show }) => show)
    .map(({ key, href, title, icon }) => (
      <ExternalLink key={key} href={href} className={css.icon} title={title}>
        {icon}
      </ExternalLink>
    ));
};

const Footer = props => {
  const { rootClassName, className, intl, isSymptomPage, hideNewsLetter = false } = props;
  const config = useConfiguration();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const currentUser = useSelector(selectCurrentUser);
  const [inProgress, setInProgress] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const { setConsent, toggleBanner, options } = useConsent();

  const isHomePage =
    location.pathname === '/' ||
    location.pathname === '/en/' ||
    location.pathname === '/de/' ||
    location.pathname === '/el/';

  const successMessage = intl.formatMessage({ id: 'Footer.successMessage' });
  const errorMessage = intl.formatMessage({ id: 'Footer.errorMessage' });
  const invalidEmailMessage = intl.formatMessage({ id: 'Footer.invalidEmailMessage' });
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const useHistoryPush = (page, params = {}) => {
    history.push(createResourceLocatorString(page, routeConfiguration(), params, {}));
  };

  const toasterProps = {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'colored',
    transition: Flip,
  };

  const onTriggerToast = (type, message) => {
    const toastType = type === STATUS.SUCCESS ? toast.success : toast.error;
    toastType(message, toasterProps);
  };

  const socialMediaLinks = renderSocialMediaLinks(intl, config);
  const classes = classNames(rootClassName || css.root, className);

  const [email, setEmail] = useState('');

  const isValidEmail = email => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      onTriggerToast(STATUS.ERROR, invalidEmailMessage || 'Please enter a valid email address.');
      return;
    }
    setInProgress(true);
    try {
      const subscription = await dispatch(subscribeToNewsletter({ email }));
      if (subscription.status === 'success') {
        dispatch(
          updateProfile({
            protectedData: {
              newsLetterConsent: true,
            },
          })
        );
      }
      onTriggerToast(subscription?.status, successMessage);
      setEmail('');
      setIsChecked(false);
    } catch (error) {
      onTriggerToast(STATUS.ERROR, errorMessage);
    } finally {
      setInProgress(false);
    }
  };

  const handleEmailChange = event => {
    setEmail(event.target.value);
  };

  // Handle opening the consent modal using the global bridge
  const handleOpenConsent = e => {
    e?.preventDefault?.();

    if (typeof window !== 'undefined' && typeof window.__openConsent === 'function') {
      if (currentUser) {
        window.__openConsent();
      } else {
        const mandatory = options.services
          .filter(service => service?.mandatory)
          .map(service => service.id);
        setConsent(mandatory);
        toggleBanner();
      }
    }
  };

  const handleOurPlansClick = () => {
    const landingPath = createResourceLocatorString('LandingPage', routeConfiguration(), {}, {});
    history.push({ pathname: landingPath, hash: '#sectionOurPlan' });
  };

  const handleOurExpertsClick = () => {
    const landingPath = createResourceLocatorString('LandingPage', routeConfiguration(), {}, {});
    history.push({ pathname: landingPath, hash: '#sectionOurExperts' });
  };

  const renderNewsletter = !hideNewsLetter ? (
    <div className={css.bottomSectionContent}>
      <div className={css.contentWidth}>
        <div className={css.newsletterSec}>
          <div className={css.joinNowcontent}>
            <img src={footMail} alt="mail" />
            <h2>
              <FormattedMessage id="Footer.joinNewsletter" />
            </h2>
            <p>
              <FormattedMessage id="Footer.noSpam" />
            </p>
          </div>
          <div className={css.subscribeNow}>
            <div className={css.subscribe}>
              <form onSubmit={handleSubmit}>
                <div className={css.checkboxContainer}>
                  <input
                    type="checkbox"
                    id="newsletterCheckbox"
                    checked={isChecked}
                    onChange={event => setIsChecked(event.target.checked)}
                  />
                  <label htmlFor="newsletterCheckbox">
                    {intl.formatMessage({ id: 'Footer.checkboxMessage' })}
                  </label>
                </div>
                <div className={css.formRow}>
                  <div className={css.subscribeInput}>
                    <input
                      type="email"
                      placeholder={intl.formatMessage({ id: 'Footer.newsletterPlaceholder' })}
                      value={email}
                      onChange={handleEmailChange}
                      required
                    />
                  </div>
                  <Button type="submit" inProgress={inProgress} disabled={!email || !isChecked}>
                    <FormattedMessage id="Footer.subscribe" />
                    <Icons name="heartUnfilled" />
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const investors = [
    {
      imageUrl: eleven,
      altText: 'Nikolaos Vlahos, M.D, F.A.C.O.G.',
      websiteUrl: 'https://www.11.vc/',
      className: css.eleven,
    },
    {
      imageUrl: frc,
      altText: 'Beth Hirschhorn',
      websiteUrl: 'https://www.filrougecapital.com/',
      className: css.frc,
    },
    {
      imageUrl: european,
      altText: 'Dr. Sabine Wünschmann',
      websiteUrl: 'https://european-union.europa.eu/index_en',
      className: css.eu,
    },
  ];

  const InvestorElement = ({ imageUrl, altText, websiteUrl, className }) => {
    return (
      <div className={css.team}>
        <div className={css.teamInfo}>
          <div className={classNames(css.investorImg, className)}>
            <ExternalLink href={websiteUrl}>
              <img src={imageUrl} alt={altText} />
            </ExternalLink>
          </div>
        </div>
      </div>
    );
  };

  const handleContactUsClick = () => {
    trackAContactUsClicked(MIXPANEL_EVENTS?.CONTACT_FOOTER);
    useHistoryPush('ContactPage');
  };

  return (
    <div className={classes}>
      {isSymptomPage ? null : <>{renderNewsletter}</>}
      <div className={css.topSectionContent}>
        <div className={css.contentWidth}>
          <div className={css.content}>
            <div className={css.leftSec}>
              <div className={css.links}>
                <NamedLink name="LandingPage" className={css.logoLink}>
                  <Logo format="desktop" className={css.logo} />
                </NamedLink>
                <NamedLink name="ContactPage" className={css.contactUs}>
                  <FormattedMessage id="Footer.contactUs" />
                </NamedLink>
                <div className={css.email}>
                  <a href="mailto:info@vivianlab.com" target="_blank" rel="noreferrer">
                    <FormattedMessage id="Footer.email" />
                  </a>
                </div>
                <p className={css.poweredby}>
                  <a href="https://stripe.com" target="_blank" rel="noreferrer">
                    <FormattedMessage id="Footer.securePayment" />
                  </a>
                </p>
                <div className={css.paymentAccepted}>
                  <Icons name="visa" />
                  <Icons name="mastercard" />
                </div>
              </div>
            </div>
            <div className={css.rightSec}>
              <div className={css.rightSecContent}>
                <div className={css.links}>
                  <h2>
                    <FormattedMessage id="Footer.support" />
                  </h2>
                  <ul className={css.list}>
                    <li className={css.listItem}>
                      <NamedLink name="FAQPage" className={css.link}>
                        <FormattedMessage id="Footer.faq" />
                      </NamedLink>
                    </li>
                    <li className={css.listItem}>
                      <div name="ContactPage" onClick={handleContactUsClick} className={css.link}>
                        <FormattedMessage id="Footer.contactUs" />
                      </div>
                    </li>
                  </ul>
                </div>

                <div className={css.links}>
                  <h2>
                    <FormattedMessage id="Footer.getToKnowUs" />
                  </h2>
                  <ul className={css.list}>
                    <li className={css.listItem}>
                      <NamedLink name="BlogPage" className={css.link}>
                        <FormattedMessage id="Footer.blog" />
                      </NamedLink>
                    </li>
                  </ul>
                </div>

                <div className={css.links}>
                  <h2>
                    <FormattedMessage id="Footer.company" />
                  </h2>
                  <ul className={css.list}>
                    <li className={css.listItem}>
                      <NamedLink name="AboutPage" className={css.link}>
                        <FormattedMessage id="Footer.toAboutPage" />
                      </NamedLink>
                    </li>
                    {getDefaultLocale() === 'en' && (
                      <li className={css.listItem}>
                        <ExternalLink
                          href="https://fate-paw-4d0.notion.site/Vivian-Lab-Anti-Corruption-and-Bribery-Policy-e3a88738a3914a68804de77ab973b11f"
                          className={css.link}
                        >
                          <FormattedMessage id="Footer.anticorruptionPolicy" />
                        </ExternalLink>
                      </li>
                    )}
                    <li className={css.listItem}>
                      <NamedLink name="DataPrivacyPage" className={css.link}>
                        <FormattedMessage id="Footer.dataPrivacyPage" />
                      </NamedLink>
                    </li>
                    <li className={css.listItem}>
                      <NamedLink name="TermsOfServicePage" className={css.link}>
                        <FormattedMessage id="Footer.privacyPolicy" />
                      </NamedLink>
                    </li>
                    <li className={css.listItem}>
                      <NamedLink name="PrivacyPolicyPage" className={css.link}>
                        <FormattedMessage id="Footer.termsOfUse" />
                      </NamedLink>
                    </li>
                    <li className={css.listItem}>
                      <NamedLink name="CookiePolicy" className={css.link}>
                        <FormattedMessage id="Footer.cookiePolicy" />
                      </NamedLink>
                    </li>
                    <li className={css.listItem}>
                      <button
                        type="button"
                        className={classNames(css.link, css.linkButton)}
                        onClick={handleOpenConsent}
                        aria-label={intl.formatMessage({
                          id: 'Footer.cookiesPreferences',
                          defaultMessage: 'Cookies Preferences',
                        })}
                      >
                        <FormattedMessage
                          id="Footer.cookiesPreferences"
                          defaultMessage="Cookies Preferences"
                        />
                      </button>
                    </li>
                    {getDefaultLocale() === 'de' && (
                      <li className={css.listItem}>
                        <NamedLink name="Impressum" className={css.link}>
                          <FormattedMessage id="Footer.impressum" />
                        </NamedLink>
                      </li>
                    )}
                  </ul>
                </div>

                <div className={css.links}>
                  <h2>
                    <FormattedMessage id="Footer.followUs" />
                  </h2>
                  {socialMediaLinks.length > 0 ? (
                    <div className={css.socialLinks}>{socialMediaLinks}</div>
                  ) : null}
                </div>
              </div>

              <div className={css.investorsSec}>
                {isHomePage && (
                  <h2 className={css.investorsTitle}>
                    <FormattedMessage id="Footer.investors" />
                  </h2>
                )}
                {isHomePage && (
                  <div className={css.investors}>
                    {investors.map((member, index) => (
                      <InvestorElement key={index} {...member} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={css.copyright}>
            <FormattedMessage id="Footer.copyright" />
          </div>
        </div>
      </div>
    </div>
  );
};

Footer.defaultProps = {
  rootClassName: null,
  className: null,
};

Footer.propTypes = {
  rootClassName: string,
  className: string,
  intl: intlShape.isRequired,
};

export default injectIntl(Footer);
